import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Switch,
} from "antd";
import { EditOutlined, HomeOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Spinner } from "reactstrap";
import SeriesForm from "./ModelsSeriesForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import { addSeries, deleteSeries, getSeries, updateSeries } from "../../store/actions/series.js";
import { getBrands } from "../../store/actions/brand.js";
import moment from "moment-timezone";

const { Title } = Typography;

const Series = () => {
  document.title = "Series";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();
  const seriesList = useSelector((state) => state.series.list);
  const totalCount = useSelector((state) => state.series.totalCount ?? 0);
  const brandList = useSelector((state) => state.brand.list);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({});

  // Build a map for quick brand name lookup
  const brandMap = brandList.reduce((acc, brand) => {
    acc[brand.id] = brand.name;
    return acc;
  }, {});

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getBrands());
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const fetchSeriesData = async () => {
      setIsLoading(true);
      try {
        await dispatch(getSeries({ page: currentPage, search: searchText, ...filters }));
      } catch (error) {
        console.error("Fetch series error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSeriesData();
  }, [dispatch, currentPage, searchText, filters]);

  // ─── Create / Update ────────────────────────────────────────────────────────
  const handleCreate = async (values) => {
    if (selectedRecord) {
      // Only send changed fields
      const changedFields = {};

      if (values.name !== selectedRecord.name) {
        changedFields.name = values.name;
      }
      if (values.brandId !== selectedRecord.brandid) {
        changedFields.brandid = Number(values.brandId);
      }
      if (values.ordinal !== selectedRecord.ordinal) {
        changedFields.ordinal = values.ordinal;
      }

      changedFields.id = selectedRecord.id; // always send id

      await dispatch(updateSeries(changedFields));
    } else {
      // CREATE — send all fields
      const payload = {
        name: values.name,
        brandid: Number(values.brandId),
        isForAll: values.isForAll ?? false,
        ...(values.ordinal !== undefined && values.ordinal !== null && { ordinal: values.ordinal }),
      };

      await dispatch(addSeries(payload));
    }

    await dispatch(getSeries(currentPage));
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    await dispatch(deleteSeries(id));
    await dispatch(getSeries(currentPage));
  };

  // ─── Toggle status ───────────────────────────────────────────────────────────
  const handleChangeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    await dispatch(updateSeries({ id, status: newStatus }));
    await dispatch(getSeries(currentPage));
  };

  // ─── Open edit modal ─────────────────────────────────────────────────────────
  const handleEdit = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  // ─── Columns ─────────────────────────────────────────────────────────────────
  let columns = [
    {
      title: "Series Name",
      dataIndex: "name",
      key: "name",
      sorter: stringSorter("name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
      sorter: stringSorter("brandName"),
      sortDirections: ["ascend", "descend"],
      render: (brandName) => brandName || "-",
    },
    {
      title: "Ordinal",
      dataIndex: "ordinal",
      key: "ordinal",
      sorter: (a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0),
      sortDirections: ["ascend", "descend"],
      render: (val) => (val !== undefined && val !== null ? val : "—"),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid() ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "",
      sorter: (a, b) =>
        moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
          ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Series</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Series
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Series
              </Button>
            </Col>
          </Row>

            <DataTable
              dataSource={seriesList.map((item) => ({ ...item, key: item.id }))}
              columns={columns}
              backendPagination={true}
              totalRecords={totalCount}
              page={currentPage}
              limit={20}
              onPageChange={setCurrentPage}
              onSearch={(text) => {
                setSearchText(text);
                setCurrentPage(1);
              }}
              onFilterChange={(newFilters) => {
                const cleaned = {};
                if (newFilters.brandid !== undefined) {
                  cleaned.brandid = newFilters.brandid;
                }
                setFilters(cleaned);
                setCurrentPage(1);
              }}
              filterBy={[
                {
                  label: "Brand",
                  field: "brandid",
                  fieldType: "SELECT",
                  options: brandList.map((brand) => ({ id: brand.id, name: brand.name })),
                },
              ]}
              loading={isLoading}
            />
        </Space>
      </Card>

      <SeriesForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
      />
    </div>
  );
};

export default Series;